import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { h as handleTabIndex } from './index8.js';
import { d as defineCustomElement$5 } from './box.js';
import { d as defineCustomElement$4 } from './form-field.js';
import { d as defineCustomElement$3 } from './icon.js';
import { d as defineCustomElement$2 } from './tooltip.js';
import { d as defineCustomElement$1 } from './tooltip-label.js';

const toggleCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:-webkit-fit-content;width:fit-content}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{align-items:center;cursor:pointer;display:flex;flex:none;flex-direction:row;gap:.25rem;height:1.25rem;width:-webkit-fit-content;width:fit-content}.label{font-family:Open Sans;font-size:.75rem;font-weight:700;letter-spacing:.03em;line-height:1rem;text-transform:uppercase;-webkit-user-select:none;user-select:none;width:24px}.box,.label{display:block}.box{background-color:var(--jw-theme-system-primary-default-bg-color);border-radius:624.9375rem;height:100%;overflow:hidden;position:relative;transition:background-color .2s ease;width:2.5rem}.handle{bottom:.125rem;left:.125rem;position:absolute;right:.125rem;top:.125rem;transition:transform .2s ease}.handle:after{background-color:var(--jw-theme-form-field-disabled-inverted-bg-color);border-radius:50%;content:\"\";height:1rem;left:0;position:absolute;top:0;width:1rem}:host([position=left]) .inner{flex-direction:row-reverse}:host([position=left]) .label{text-align:right}:host([checked]) .box{background-color:var(--jw-theme-system-primary-default-bg-color)}:host([checked]) .handle{transform:translateX(calc(100% - 1rem))}:host([checked]) .handle:after{background-color:var(--jw-theme-system-primary-default-color)}:host([checked]) .label-on{color:var(--jw-theme-system-surface-color);display:block}:host([checked]) .label-off{display:none}:host(:not([checked])) .box{background-color:var(--jw-theme-form-field-disabled-inverted-bg-color)}:host(:not([checked])) .handle:after{background-color:var(--jw-theme-form-field-disabled-inverted-color)}:host(:not([checked])) .label-on{display:none}:host(:not([checked])) .label-off{color:var(--jw-theme-surface-text-helper-color);display:block}:host([disabled]) .inner{cursor:not-allowed}:host([disabled]) .box{background-color:var(--jw-theme-form-field-disabled-inverted-bg-color)!important}:host([disabled]) .label{color:var(--jw-theme-surface-text-helper-color)}:host([disabled]) .handle:after{background-color:var(--jw-theme-form-field-disabled-color)}:host(:not([disabled]):focus){outline:none}:host(:not([disabled]):focus) .box{outline:var(--jw-theme-effects-focus-default-outline)}";
const WuiToggleStyle0 = toggleCss;

const WuiToggle = /*@__PURE__*/ proxyCustomElement(class WuiToggle extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        //////// FORM FIELD END ////////
        //////// TAB FOCUSABLE START ////////
        this._tabIndex = 0;
        this.handleChange = (e) => {
            e.stopPropagation();
            if (this.disabled) {
                return;
            }
            // The input element determines the behavior
            const target = this.inputElm;
            if (!target) {
                return;
            }
            this.dirty = true;
            this.checked = target.checked;
            this.checkValidity();
            this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.name = undefined;
        this.checked = undefined;
        this.disabled = undefined;
        this.position = 'right';
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    async _handleKeyDown(e) {
        if (this.disabled) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            // Forward event to hidden input
            this.inputElm?.click();
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
    }
    componentWillRender() {
        handleTabIndex(this);
    }
    renderInner() {
        return (h("label", { class: "inner" }, h("div", { class: "box" }, h("input", { hidden: true, type: "checkbox", name: this.name, value: this.value, checked: this.checked, disabled: this.disabled, onInput: this.handleChange, required: !!this.required, onChange: (e) => {
                // We want to use the input event, not 'change'
                e.stopPropagation();
            }, ref: (el) => {
                this.inputElm = el;
            } }), h("div", { class: "handle" })), h("span", { class: "label label-on", "aria-hidden": this.checked ? 'false' : 'true' }, "ON"), h("span", { class: "label label-off", "aria-hidden": this.checked ? 'true' : 'false' }, "OFF")));
    }
    render() {
        return (h(Host, { key: 'fb45320a1d700d1edf6fb2eef1836da94968e1ed', ...getHostAttributes(this), "aria-checked": this.checked ? 'true' : 'false', role: "switch" }, h("wui-form-field", { key: '1c9902e9d8afca3f122afb8dcb8f2a31b75f3ec5', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get el() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiToggleStyle0; }
}, [1, "wui-toggle", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "name": [1],
        "checked": [1540],
        "disabled": [516],
        "position": [513],
        "customValidation": [32],
        "showValidity": [32]
    }, [[0, "keydown", "_handleKeyDown"]], {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-toggle", "wui-box", "wui-form-field", "wui-icon", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-toggle":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiToggle);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiToggle as W, defineCustomElement as d };

//# sourceMappingURL=toggle.js.map